/* VastraGharr Luxe 3.0 - Search overlay 2.0
   <vg-search-panel> lives inside each header details-modal (snippets/header-search.liquid).
   - Before typing: recent searches (localStorage "vg:searches", max 6, clearable), trending searches,
     popular collection chips and recently viewed products (localStorage "vg:recent").
   - While typing: Dawn's predictive-search renders results; the discover panel steps aside.
   - Focus trap that follows dynamic content, Esc (Dawn's details-modal), body scroll lock (details-modal),
     aria-expanded on the toggle, header stacking above the announcement bar while open.
   Shared helpers used by the cart (assets/vg-cart.js): VG.formatMoney, VG.productMini.
*/
(function () {
  'use strict';

  var VG = (window.VG = window.VG || {});
  if (VG.searchScriptLoaded) return;
  VG.searchScriptLoaded = true;

  var SEARCHES_KEY = 'vg:searches';
  var RECENT_KEY = 'vg:recent';
  var MAX_SEARCHES = 6;
  var MAX_VIEWED = 6;
  var CACHE_PREFIX = 'vg:pmini:';
  var CACHE_TTL = 30 * 60 * 1000;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (shopRoot.charAt(shopRoot.length - 1) !== '/') shopRoot += '/';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function readJSON(key, fallback) {
    try {
      var raw = window.localStorage.getItem(key);
      if (raw === null) return fallback;
      var value = JSON.parse(raw);
      return value === null || value === undefined ? fallback : value;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  /* ---------- Header config (money format, fly-to-bag flag) ---------- */
  var headerConfig = null;
  VG.headerConfig = function () {
    if (headerConfig) return headerConfig;
    var node = document.querySelector('script[data-vg-header-config]');
    try {
      headerConfig = node ? JSON.parse(node.textContent) : {};
    } catch (e) {
      headerConfig = {};
    }
    return headerConfig;
  };

  /* ---------- Money (Shopify money formats, trailing zeros dropped like money_without_trailing_zeros) ---------- */
  function groupNumber(number, decimals, thousands, decimal) {
    var fixed = (Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals)).toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] && parts[1] !== '00' && !/^0+$/.test(parts[1]) ? decimal + parts[1] : '';
    return whole + cents;
  }

  VG.formatMoney =
    VG.formatMoney ||
    function (cents, format) {
      var value = (typeof cents === 'string' ? parseFloat(cents.replace(/[^\d.-]/g, '')) : Number(cents)) || 0;
      value = value / 100;
      var pattern = String(format || VG.headerConfig().moneyFormat || 'Rs. {{amount}}').replace(/<[^>]*>/g, '');
      var match = pattern.match(/\{\{\s*(\w+)\s*\}\}/);
      var type = match ? match[1] : 'amount';
      var out;
      switch (type) {
        case 'amount_no_decimals':
          out = groupNumber(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          out = groupNumber(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          out = groupNumber(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          out = groupNumber(value, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          out = groupNumber(value, 2, ' ', ',');
          break;
        default:
          out = groupNumber(value, 2, ',', '.');
      }
      return match ? pattern.replace(match[0], out) : out;
    };

  /* ---------- Product mini tiles (recently viewed) ---------- */
  var miniMemory = {};

  function cacheGet(handle) {
    if (miniMemory[handle]) return miniMemory[handle];
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > CACHE_TTL) return null;
      miniMemory[handle] = entry.p;
      return entry.p;
    } catch (e) {
      return null;
    }
  }

  function cacheSet(handle, product) {
    miniMemory[handle] = product;
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify({ t: Date.now(), p: product }));
    } catch (e) {
      /* storage full or blocked: memory cache still works */
    }
  }

  function pruneRecent(handle) {
    var list = readJSON(RECENT_KEY, []);
    if (!Array.isArray(list)) return;
    var next = list.filter(function (item) {
      return item !== handle;
    });
    if (next.length !== list.length) writeJSON(RECENT_KEY, next);
  }

  function imageUrl(src, width) {
    if (!src) return '';
    var url = String(src);
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  VG.productMini = VG.productMini || {
    recentHandles: function (limit, exclude) {
      var list = readJSON(RECENT_KEY, []);
      if (!Array.isArray(list)) return [];
      var seen = {};
      return list
        .filter(function (handle) {
          if (typeof handle !== 'string' || !handle || seen[handle] || handle === exclude) return false;
          seen[handle] = true;
          return true;
        })
        .slice(0, limit || MAX_VIEWED);
    },

    fetch: function (handle) {
      var cached = cacheGet(handle);
      if (cached) return Promise.resolve(cached);
      return fetch(shopRoot + 'products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then(function (response) {
          if (response.status === 404) {
            pruneRecent(handle);
            return null;
          }
          return response.ok ? response.json() : null;
        })
        .then(function (data) {
          if (!data || !data.handle) return null;
          var product = {
            handle: data.handle,
            title: data.title,
            url: data.url || shopRoot + 'products/' + data.handle,
            price: data.price,
            compare: data.compare_at_price || 0,
            varies: !!data.price_varies,
            available: data.available !== false,
            image: data.featured_image || '',
            alt: (data.media && data.media[0] && data.media[0].alt) || data.title,
          };
          cacheSet(handle, product);
          return product;
        })
        .catch(function () {
          return null;
        });
    },

    list: function (handles) {
      return Promise.all(handles.map(this.fetch)).then(function (items) {
        return items.filter(Boolean);
      });
    },

    /* Returns an <li class="vg-mini"> tile. opts.sizes: image sizes attribute. */
    render: function (product, opts) {
      var options = opts || {};
      var li = document.createElement('li');
      li.className = 'vg-mini' + (product.available ? '' : ' is-sold-out');

      var link = document.createElement('a');
      link.className = 'vg-mini__link';
      link.href = product.url;

      var media = document.createElement('span');
      media.className = 'vg-mini__media vg-img';
      if (product.image) {
        var img = document.createElement('img');
        img.src = imageUrl(product.image, 240);
        img.srcset = [120, 180, 240, 360]
          .map(function (w) {
            return imageUrl(product.image, w) + ' ' + w + 'w';
          })
          .join(', ');
        img.sizes = options.sizes || '(min-width: 990px) 140px, 112px';
        img.alt = displayTitle(product.alt || product.title || '');
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 240;
        img.height = 320;
        media.appendChild(img);
      }
      link.appendChild(media);

      var title = document.createElement('span');
      title.className = 'vg-mini__title';
      title.textContent = displayTitle(product.title);
      link.appendChild(title);

      var price = document.createElement('span');
      price.className = 'vg-mini__price';
      var now = document.createElement('span');
      now.className = 'vg-mini__now' + (product.compare > product.price ? ' is-sale' : '');
      now.textContent = (product.varies ? 'From ' : '') + VG.formatMoney(product.price);
      price.appendChild(now);
      if (product.compare > product.price) {
        var was = document.createElement('s');
        was.className = 'vg-mini__was';
        was.textContent = VG.formatMoney(product.compare);
        price.appendChild(was);
      }
      if (!product.available) {
        var sold = document.createElement('span');
        sold.className = 'vg-mini__sold';
        sold.textContent = 'Sold out';
        price.appendChild(sold);
      }
      link.appendChild(price);
      li.appendChild(link);
      return li;
    },
  };

  /* Store titles arrive in all caps or all lower case; show them in title case like the product cards. */
  function displayTitle(title) {
    var text = String(title || '').trim();
    if (!text || (text !== text.toUpperCase() && text !== text.toLowerCase())) return text;
    return text.toLowerCase().replace(/(^|\s)([a-z])/g, function (match, lead, letter) {
      return lead + letter.toUpperCase();
    });
  }
  VG.displayTitle = VG.displayTitle || displayTitle;

  function currentProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  /* ---------- Recent searches ---------- */
  VG.searches = VG.searches || {
    get: function () {
      var list = readJSON(SEARCHES_KEY, []);
      if (!Array.isArray(list)) return [];
      return list
        .filter(function (term) {
          return typeof term === 'string' && term.trim();
        })
        .slice(0, MAX_SEARCHES);
    },
    add: function (term) {
      var clean = String(term || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 60);
      if (clean.length < 2) return;
      var lower = clean.toLowerCase();
      var list = [clean].concat(
        this.get().filter(function (item) {
          return item.toLowerCase() !== lower;
        })
      );
      writeJSON(SEARCHES_KEY, list.slice(0, MAX_SEARCHES));
      document.dispatchEvent(new CustomEvent('vg:searches:change'));
    },
    clear: function () {
      try {
        window.localStorage.removeItem(SEARCHES_KEY);
      } catch (e) {
        /* nothing stored */
      }
      document.dispatchEvent(new CustomEvent('vg:searches:change'));
    },
  };

  function searchUrl(term) {
    var base = (VG.routes && VG.routes.search) || shopRoot + 'search';
    return base + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
  }

  /* Record the term of a search results page (covers the search page form, chips and bottom-nav searches). */
  function recordSearchPage() {
    if (!/\/search\/?$/.test(window.location.pathname)) return;
    var params = new URLSearchParams(window.location.search);
    var term = params.get('q');
    if (term) VG.searches.add(term);
  }

  function isVisible(el) {
    return !!(el && el.getClientRects().length && window.getComputedStyle(el).visibility !== 'hidden');
  }

  /* ---------- <vg-search-panel> ---------- */
  if (!customElements.get('vg-search-panel')) {
    customElements.define(
      'vg-search-panel',
      class VgSearchPanel extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.modal = this.closest('details-modal');
          this.details = this.closest('details');
          this.dialog = this.closest('.search-modal');
          this.summary = this.details ? this.details.querySelector('summary') : null;
          this.head = this.querySelector('[data-vg-search-head]');
          this.input = this.querySelector('input[type="search"]');
          this.form = this.input ? this.input.form : null;
          this.recent = this.querySelector('[data-vg-search-recent]');
          this.recentList = this.querySelector('[data-vg-search-recent-list]');
          this.viewed = this.querySelector('[data-vg-search-viewed]');
          this.viewedList = this.querySelector('[data-vg-search-viewed-list]');
          if (!this.details || !this.input) return;

          this.onToggle = this.onToggle.bind(this);
          this.onInput = this.onInput.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onClick = this.onClick.bind(this);
          this.onSubmit = this.onSubmit.bind(this);
          this.onReset = this.onReset.bind(this);
          this.onSearchesChange = this.renderRecent.bind(this);
          this.onResize = this.measure.bind(this);

          this.details.addEventListener('toggle', this.onToggle);
          this.input.addEventListener('input', this.onInput);
          this.addEventListener('keydown', this.onKeydown);
          this.addEventListener('click', this.onClick);
          if (this.form) {
            this.form.addEventListener('submit', this.onSubmit);
            this.form.addEventListener('reset', this.onReset);
          }
          document.addEventListener('vg:searches:change', this.onSearchesChange);

          this.syncTyping();
          if (this.details.hasAttribute('open')) this.onToggle();
        }

        disconnectedCallback() {
          if (!this.ready) return;
          this.ready = false;
          if (this.details) this.details.removeEventListener('toggle', this.onToggle);
          if (this.input) this.input.removeEventListener('input', this.onInput);
          this.removeEventListener('keydown', this.onKeydown);
          this.removeEventListener('click', this.onClick);
          if (this.form) {
            this.form.removeEventListener('submit', this.onSubmit);
            this.form.removeEventListener('reset', this.onReset);
          }
          document.removeEventListener('vg:searches:change', this.onSearchesChange);
          window.removeEventListener('resize', this.onResize);
          if (this.headObserver) this.headObserver.disconnect();
          this.setHeaderRaised(false);
        }

        isOpen() {
          return !!(this.details && this.details.hasAttribute('open'));
        }

        onToggle() {
          var open = this.isOpen();
          if (this.summary) this.summary.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.setHeaderRaised(open);
          if (open) {
            document.documentElement.classList.add('vg-search-is-open');
            this.renderRecent();
            this.renderViewed();
            this.syncTyping();
            this.measure();
            window.addEventListener('resize', this.onResize, { passive: true });
            if (!this.headObserver && 'ResizeObserver' in window && this.head) {
              this.headObserver = new ResizeObserver(this.onResize);
              this.headObserver.observe(this.head);
            }
            // details-modal focuses the input; keep it there on touch devices that lose it during the open animation.
            window.requestAnimationFrame(() => {
              if (this.isOpen() && !this.contains(document.activeElement)) this.input.focus({ preventScroll: true });
            });
          } else {
            if (!document.querySelector('details-modal.header__search details[open]')) {
              document.documentElement.classList.remove('vg-search-is-open');
            }
            window.removeEventListener('resize', this.onResize);
            if (this.headObserver) {
              this.headObserver.disconnect();
              this.headObserver = null;
            }
          }
        }

        /* The overlay is position:fixed inside the sticky header section, so lift that section above
           the announcement bar and other header-group sections while the overlay is open. */
        setHeaderRaised(raised) {
          var section = this.closest('.section-header') || document.querySelector('.section-header');
          if (section) section.classList.toggle('vg-search-open', raised);
        }

        measure() {
          if (!this.head) return;
          var height = Math.ceil(this.head.getBoundingClientRect().height);
          if (height) this.style.setProperty('--vg-search-head', height + 'px');
        }

        syncTyping() {
          var typing = !!(this.input && this.input.value.trim().length);
          this.classList.toggle('is-typing', typing);
        }

        onInput() {
          this.syncTyping();
        }

        onReset() {
          // search-form.js prevents the native reset and clears the value itself; read it after that runs.
          window.setTimeout(() => this.syncTyping(), 0);
        }

        onSubmit(event) {
          var term = this.input.value.trim();
          if (!term) {
            event.preventDefault();
            this.input.focus();
            return;
          }
          VG.searches.add(term);
        }

        onClick(event) {
          var target = event.target;
          if (!target || typeof target.closest !== 'function') return;

          if (target.closest('[data-vg-search-clear]')) {
            event.preventDefault();
            VG.searches.clear();
            this.input.focus({ preventScroll: true });
            return;
          }

          var chip = target.closest('[data-vg-search-term]');
          if (chip) {
            VG.searches.add(chip.getAttribute('data-vg-search-term'));
            return;
          }

          // A predictive result was chosen: remember what the shopper typed.
          if (target.closest('#predictive-search-results a, #predictive-search-results button')) {
            var typed = this.input.value.trim();
            if (typed) VG.searches.add(typed);
          }
        }

        /* Focus trap that follows dynamic content (recent chips, predictive results, recently viewed). */
        onKeydown(event) {
          if (event.key !== 'Tab' || !this.isOpen()) return;
          var scope = this.dialog || this;
          var items = toArray(scope.querySelectorAll(FOCUSABLE)).filter(function (el) {
            return !el.closest('[hidden]') && isVisible(el);
          });
          if (!items.length) return;
          event.preventDefault();
          event.stopPropagation();
          var index = items.indexOf(document.activeElement);
          var next;
          if (event.shiftKey) next = index <= 0 ? items.length - 1 : index - 1;
          else next = index === -1 || index === items.length - 1 ? 0 : index + 1;
          items[next].focus();
        }

        renderRecent() {
          if (!this.recent || !this.recentList) return;
          var terms = VG.searches.get();
          this.recentList.textContent = '';
          terms.forEach((term) => {
            var li = document.createElement('li');
            var link = document.createElement('a');
            link.className = 'vg-search__chip vg-search__chip--recent';
            link.href = searchUrl(term);
            link.setAttribute('data-vg-search-term', term);
            var icon = document.createElement('span');
            icon.className = 'vg-search__chip-icon';
            icon.setAttribute('aria-hidden', 'true');
            icon.innerHTML =
              '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3"/><path d="M4.5 4.5v3.2h3.2"/><path d="M12 8.2V12l2.6 1.6"/></svg>';
            var text = document.createElement('span');
            text.textContent = term;
            link.appendChild(icon);
            link.appendChild(text);
            li.appendChild(link);
            this.recentList.appendChild(li);
          });
          this.recent.hidden = terms.length === 0;
        }

        renderViewed() {
          if (!this.viewed || !this.viewedList || !VG.productMini) return;
          var handles = VG.productMini.recentHandles(MAX_VIEWED, currentProductHandle());
          var key = handles.join(',');
          if (!handles.length) {
            this.viewed.hidden = true;
            this.viewedKey = '';
            return;
          }
          if (key === this.viewedKey) return;
          this.viewedKey = key;
          VG.productMini.list(handles).then((products) => {
            if (this.viewedKey !== key) return;
            this.viewedList.textContent = '';
            products.forEach((product) => {
              this.viewedList.appendChild(VG.productMini.render(product, { sizes: '(min-width: 990px) 150px, (min-width: 750px) 130px, 112px' }));
            });
            this.viewed.hidden = products.length === 0;
            this.classList.toggle('has-viewed', products.length > 0);
          });
        }
      }
    );
  }

  /* Keep the toggle state right when the theme editor re-renders the header. */
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('vg-search-panel')) {
      document.documentElement.classList.remove('vg-search-is-open');
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', recordSearchPage);
  else recordSearchPage();
})();
